import { isUserLogin } from '@eureka/utils/account';

/**
 * API路径解析器
 * 根据用户登录状态自动选择正确的API路径
 */
export class ApiPathResolver {
    private static instance: ApiPathResolver;
    private loginStatusCache: boolean | null = null;
    private cacheExpiry: number = 0;
    private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

    private constructor() {}

    static getInstance(): ApiPathResolver {
        if (!ApiPathResolver.instance) {
            ApiPathResolver.instance = new ApiPathResolver();
        }
        return ApiPathResolver.instance;
    }

    /**
     * 获取用户登录状态（带缓存和超时保护）
     */
    private async getLoginStatus(): Promise<boolean> {
        const now = Date.now();

        // 如果缓存有效，直接返回缓存结果
        if (this.loginStatusCache !== null && now < this.cacheExpiry) {
            return this.loginStatusCache;
        }

        try {
            // 添加超时保护，避免长时间阻塞
            const loginStatusPromise = isUserLogin();
            const timeoutPromise = new Promise<boolean>((_, reject) => {
                setTimeout(() => reject(new Error('Login status check timeout')), 300000);
            });

            this.loginStatusCache = await Promise.race([loginStatusPromise, timeoutPromise]);
            this.cacheExpiry = now + this.CACHE_DURATION;
            return this.loginStatusCache;
        } catch (error) {
            console.warn('[ApiPathResolver] Failed to get login status, defaulting to guest mode:', error);
            // 出错时默认为未登录状态
            this.loginStatusCache = false;
            this.cacheExpiry = now + this.CACHE_DURATION;
            return false;
        }
    }

    /**
     * 清除登录状态缓存
     * 在用户登录/登出后调用
     */
    clearCache(): void {
        this.loginStatusCache = null;
        this.cacheExpiry = 0;
    }

    /**
     * 根据登录状态解析API路径
     * @param basePath 基础路径，如 'task/init'
     * @returns 完整的API路径
     */
    async resolvePath(basePath: string): Promise<string> {
        const isLoggedIn = await this.getLoginStatus();

        // 移除开头的斜杠（如果有）
        const cleanBasePath = basePath.replace(/^\/+/, '');

        if (isLoggedIn) {
            // 已登录用户使用标准路径: /telecom/aicc/task/init
            return `/telecom/aicc/${cleanBasePath}`;
        } else {
            // 未登录用户使用游客路径: /telecom/aicc_guest/task/init
            return `/telecom/aicc_guest/${cleanBasePath}`;
        }
    }

    /**
     * 批量解析多个API路径
     * @param basePaths 基础路径数组
     * @returns 解析后的路径数组
     */
    async resolvePaths(basePaths: string[]): Promise<string[]> {
        const isLoggedIn = await this.getLoginStatus();

        return basePaths.map(basePath => {
            const cleanBasePath = basePath.replace(/^\/+/, '');

            if (isLoggedIn) {
                return `/telecom/aicc/${cleanBasePath}`;
            } else {
                return `/telecom/aicc_guest/${cleanBasePath}`;
            }
        });
    }

    /**
     * Novelty Search 流程网关（与 Vite 代理 `/telecom/novelty_search`、`/telecom/novelty_search_guest` 一致）
     * @param basePath 接口路径，如 task/init、extract_inventions
     */
    async resolveNoveltySearchPath(basePath: string): Promise<string> {
        const isLoggedIn = await this.getLoginStatus();
        const cleanBasePath = basePath.replace(/^\/+/, '');
        if (isLoggedIn) {
            return `/telecom/novelty_search/${cleanBasePath}`;
        }
        return `/telecom/novelty_search_guest/${cleanBasePath}`;
    }
}

/**
 * 获取API路径解析器实例
 */
export const getApiPathResolver = () => ApiPathResolver.getInstance();

/**
 * 便捷函数：解析单个API路径
 * @param basePath 基础路径
 * @returns 解析后的完整路径
 */
export const resolveApiPath = (basePath: string): Promise<string> => {
    return getApiPathResolver().resolvePath(basePath);
};

/**
 * 便捷函数：批量解析API路径
 * @param basePaths 基础路径数组
 * @returns 解析后的路径数组
 */
export const resolveApiPaths = (basePaths: string[]): Promise<string[]> => {
    return getApiPathResolver().resolvePaths(basePaths);
};

/** Novelty Search：`packages/novelty/.../novelty_search` 页面接口；已登录 → novelty_search，未登录 → novelty_search_guest */
export const resolveNoveltySearchApiPath = (basePath: string): Promise<string> => {
    return getApiPathResolver().resolveNoveltySearchPath(basePath);
};